import ConversationItemParts from '../store/schema/ConversationItemParts';
import ConversationReadingPaneNode from '../store/schema/ConversationReadingPaneNode';
import mailStore from '../store/Store';

export interface ParentChildRelation {
    internetMessageId: string;
    parentItemId: string;
    childrenItemIds: string[];
}

/*
 * -------------------------------------------------------------
 * itemId -> internetMessageId, parentItemId, childernItemIds[]
 * -------------------------------------------------------------
 * The current implementation is based on the assumption that we always have root node
 * available from GCI calls.
 * TODO WI 95841: Handle long conversations case where the root node might not be immediately available,
 * or some nodes are missing and obtained from load more.
 */
function getConversationItemsRelationMap(
    conversation: ConversationItemParts
): Map<string, ParentChildRelation> {
    const allConversationNodeIds: string[] = conversation?.conversationNodeIds;
    const conversationNodeIdCount = allConversationNodeIds?.length;
    if (!allConversationNodeIds || !conversationNodeIdCount || conversationNodeIdCount == 0) {
        return null;
    }
    const cloneConversationNodeIds = [...allConversationNodeIds];
    const isNewestOnBottom: boolean =
        conversation.conversationSortOrder === 'ChronologicalNewestOnBottom';

    // if sort order is newest on top, reverse the nodes in cloneConversationNodeIds to get
    // root node first and then child nodes in the order of messages sent.
    if (!isNewestOnBottom) {
        cloneConversationNodeIds.reverse();
    }

    const rootNode: ConversationReadingPaneNode = mailStore.conversationNodes.get(
        cloneConversationNodeIds[0]
    );
    // initialize map with root node
    const conversationItemsRelationMap: Map<string, ParentChildRelation> = new Map();
    conversationItemsRelationMap.set(rootNode.itemIds?.[0], {
        internetMessageId: rootNode.internetMessageId,
        parentItemId: null,
        childrenItemIds: [],
    });
    let index = 1; // skip root node
    while (index < conversationNodeIdCount) {
        const node: ConversationReadingPaneNode = mailStore.conversationNodes.get(
            cloneConversationNodeIds[index]
        );
        updateConversationRelationMapWithNode(conversationItemsRelationMap, node);
        index += 1;
    }
    return conversationItemsRelationMap;
}

/**
 * Helper function to check if mailstore contains conversation and nodes
 */
export function isConversationInMailstore(conversationId: string): boolean {
    const conversationItemParts: ConversationItemParts = mailStore.conversations?.get(
        conversationId
    );
    if (!conversationItemParts) {
        return false;
    }
    const allConversationNodeIds: string[] = conversationItemParts.conversationNodeIds;
    const conversationNodeIdCount = allConversationNodeIds?.length;
    if (!allConversationNodeIds || !conversationNodeIdCount || conversationNodeIdCount == 0) {
        return false;
    }
    return true;
}

/**
 * This function is used to get the local leaf nodes from conversation tree
 */
export function getLatestItemIdsInForks(
    conversationIdString: string,
    localItemIds: string[]
): string[] {
    const conversationItemParts = mailStore.conversations?.get(conversationIdString);
    if (conversationItemParts) {
        const conversationRelationMap = getConversationItemsRelationMap(conversationItemParts);
        const latestItemIdsInForks: string[] = [];
        conversationRelationMap.forEach((relation, itemId) => {
            if (relation.childrenItemIds.length === 0) {
                // we have a leaf node, but first level expansion only shows local items
                let currentItemId = itemId;
                let currentRelation = relation;
                while (currentItemId) {
                    if (
                        localItemIds.includes(currentItemId) &&
                        currentRelation.childrenItemIds.length < 2
                    ) {
                        latestItemIdsInForks.push(currentItemId);
                        break;
                    }
                    const parentId = currentRelation.parentItemId;
                    if (parentId) {
                        currentRelation = conversationRelationMap.get(parentId);
                        currentItemId = parentId;
                    } else {
                        currentItemId = null;
                    }
                }
            }
        });
        return conversationItemParts.conversationSortOrder === 'ChronologicalNewestOnBottom'
            ? latestItemIdsInForks
            : latestItemIdsInForks.reverse();
    }
    return [];
}

export function getAncestorsOfMessageIncludingCurrentItem(
    conversationIdString: string,
    itemId: string
): string[] {
    const allAncestorIds: string[] = [itemId];
    const conversationItemParts = mailStore.conversations?.get(conversationIdString);
    if (conversationItemParts) {
        const conversationRelationMap = getConversationItemsRelationMap(conversationItemParts);
        if (!conversationRelationMap) {
            return allAncestorIds;
        }
        let currentItemId = itemId;
        while (currentItemId) {
            allAncestorIds.push(currentItemId);
            const currentItem = conversationRelationMap.get(currentItemId);
            currentItemId = currentItem?.parentItemId;
        }
    }
    return allAncestorIds;
}

/**
 * Helper function to add nodes to the map and update parent child relationships
 */
function updateConversationRelationMapWithNode(
    conversationItemsRelationMap: Map<string, ParentChildRelation>,
    node: ConversationReadingPaneNode
) {
    // TODO WI 95843: Validate for the scenario when a message is in multiple folders
    const nodeItemId = node.itemIds?.[0];
    if (!nodeItemId) {
        return null;
    }
    let parentItemId;
    for (let [itemId, relation] of conversationItemsRelationMap) {
        if (relation.internetMessageId === node.parentInternetMessageId) {
            relation.childrenItemIds.push(nodeItemId);
            parentItemId = itemId;
            break;
        }
    }
    conversationItemsRelationMap.set(nodeItemId, {
        internetMessageId: node.internetMessageId,
        parentItemId: parentItemId,
        childrenItemIds: [],
    });
}
